import { useGetLocale, useTranslate, useGo } from "@refinedev/core";
import React, { useEffect, useState } from "react";
import { Form, message } from "antd";
import styled from "styled-components";
import { authProvider, type RegisterParams } from "authProvider";
import { LockIcon } from "@phosphor-icons/react";
import { useBoundStore } from "@/store";
import { useAnalytics } from "../../hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import {
  getPrivacyPolicyLinks,
  getTermsOfServiceLinks,
  isValidLang,
} from "helpers/legal-links";
import { useFeatureFlag } from "hooks/useFeature";
import { FeatureFlags } from "@/constants";
import { SocialLogin } from "./components/SocialLogin";
import { isInAppBrowser } from "helpers/webview";
import { InAppBrowserBanner } from "./components/InAppBrowserBanner";
import { AuthPage, AuthRightPanel } from "./components/AuthLayout";
import { CoverflowCarousel } from "./components/CoverflowCarousel";
import { AuthFormPanel } from "./components/AuthFormPanel";
import { VerificationForm } from "./components/VerificationForm";
import {
  AuthInput,
  AuthSubmitButton,
  AuthLink,
  AuthLegalText,
  AuthBottomLine,
  AuthSecurityNote,
  PasswordReveal,
} from "./components/AuthFormElements";
import { usePasswordReveal } from "./utils/usePasswordReveal";
import { createEmailRule } from "./utils/emailRule";
import { t } from "i18next";
import { PASSWORD_COMPLEXITY_PATTERN } from "constants/auth";

const passwordValidator = (_: unknown, value: string) => {
  if (!value)
    return Promise.reject(
      new Error(t("pages.auth.register.pleaseEnterAPassword"))
    );
  if (value.length < 12)
    return Promise.reject(new Error(t("pages.auth.register.passwordMustBe")));

  if (!PASSWORD_COMPLEXITY_PATTERN.test(value)) {
    return Promise.reject(
      new Error(t("pages.auth.new-register.passwordMustContain"))
    );
  }

  return Promise.resolve();
};

const emptyRequiredMark = (label: React.ReactNode) => <>{label}</>;

type FormValues = {
  email: string;
  firstname: string;
  lastname: string;
  password: string;
};

export const RegisterPage: React.FC = () => {
  const t = useTranslate();
  const go = useGo();
  const [busy, setBusy] = useState(false);
  const [verificationData, setVerificationData] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const setSidebarCollapsed = useBoundStore(
    (state) => state.setSidebarCollapsed
  );
  const isLibertifySocialAuthEnabled = useFeatureFlag(
    FeatureFlags.LibertifySocialAuth
  );
  const locale = useGetLocale();
  const urlParams = new URLSearchParams(globalThis.location.search);
  const inviteId = urlParams.get("invite");
  const lang = locale()?.split("-")[0];
  const termsOfServiceLink =
    getTermsOfServiceLinks()[isValidLang(lang) ? lang : "en"];
  const privacyPolicyLink =
    getPrivacyPolicyLinks()[isValidLang(lang) ? lang : "en"];
  const { trackEvent } = useAnalytics();
  const [form] = Form.useForm<FormValues>();

  const handleCreateAccount = async () => {
    try {
      setBusy(true);
      await form.validateFields();

      const formValues = form.getFieldsValue();
      const [firstname] = formValues.email.split("@");
      const registrationData: RegisterParams = {
        email: formValues.email,
        password: formValues.password,
        firstname,
        ...(inviteId && { invite_id: inviteId }),
        application_id: "Libertify",
      };

      const response = await authProvider.register?.(registrationData);

      if (response?.success && response.redirectTo) {
        trackEvent(AnalyticsEvents.SignupComplete, {
          email: formValues.email,
          signup_method: "email",
        });
        setVerificationData({
          email: formValues.email,
          password: formValues.password,
        });
        globalThis.history.replaceState(null, "", "/verification");
      } else if (response?.error) {
        message.error(response.error.message, 7);
      }
    } catch (error) {
      console.error("Signup error:", error);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setSidebarCollapsed(true);
    return () => {
      setSidebarCollapsed(false);
    };
  }, [setSidebarCollapsed]);

  useEffect(() => {
    trackEvent(AnalyticsEvents.SignupStart, {
      signup_method: "email",
    });
  }, [trackEvent]);

  const { isPasswordVisible, handleFieldFocus, handleFieldBlur } =
    usePasswordReveal(form);

  const renderPanelContent = () => {
    if (verificationData) {
      return (
        <VerificationForm
          email={verificationData.email}
          password={verificationData.password}
        />
      );
    }

    if (isInAppBrowser()) {
      return <InAppBrowserBanner />;
    }

    return (
      <>
        {isLibertifySocialAuthEnabled && <SocialLogin action="signup" />}

        <FormRG
          form={form}
          onFinish={() => void handleCreateAccount()}
          requiredMark={emptyRequiredMark}
        >
          <Form.Item
            name="email"
            validateTrigger="onBlur"
            rules={[
              createEmailRule(t("pages.auth.register.pleaseEnterAValidEmail")),
            ]}
            style={{ margin: 0, width: "100%" }}
          >
            <AuthInput
              type="email"
              placeholder={t("pages.auth.register.emailPlaceholder")}
              autoComplete="email"
              onFocus={handleFieldFocus}
              onBlur={handleFieldBlur}
            />
          </Form.Item>

          <PasswordReveal $visible={isPasswordVisible}>
            <div>
              <Form.Item
                name="password"
                validateTrigger={[]}
                rules={[
                  {
                    validator: passwordValidator,
                  },
                ]}
                style={{ margin: 0, width: "100%" }}
              >
                <AuthInput
                  type="password"
                  placeholder={t("pages.auth.register.passwordPlaceholder")}
                  autoComplete="new-password"
                  onFocus={handleFieldFocus}
                  onBlur={handleFieldBlur}
                />
              </Form.Item>
            </div>
          </PasswordReveal>

          <Form.Item style={{ margin: 0, width: "100%" }}>
            <AuthSubmitButton type="submit" disabled={busy}>
              {busy ? "..." : t("pages.auth.register.signUp")}
            </AuthSubmitButton>
          </Form.Item>
        </FormRG>

        <AuthLegalText>
          {t("pages.auth.register.bySigningUp", {
            name: "Libertify",
          })}{" "}
          <AuthLink href={termsOfServiceLink} target="_blank">
            {t("pages.auth.register.termsOfService")}
          </AuthLink>{" "}
          {t("pages.auth.register.and")}{" "}
          <AuthLink href={privacyPolicyLink} target="_blank">
            {t("pages.auth.register.privacyPolicy")}
          </AuthLink>
        </AuthLegalText>

        <AuthBottomLine>
          {t("pages.auth.register.alreadyHaveAccount")}{" "}
          <AuthLink onClick={() => go({ to: "/login" })}>
            {t("pages.auth.register.logIn")}
          </AuthLink>
        </AuthBottomLine>

        <AuthSecurityNote>
          <LockIcon size={12} />
          {t("pages.auth.register.securityNote")}
        </AuthSecurityNote>
      </>
    );
  };

  return (
    <AuthPage>
      <CoverflowCarousel />

      <AuthRightPanel>
        <AuthFormPanel
          title={
            verificationData
              ? t("pages.auth.verification.title")
              : t("pages.auth.register.createYourAccount")
          }
        >
          {renderPanelContent()}
        </AuthFormPanel>
      </AuthRightPanel>
    </AuthPage>
  );
};

// ─── Register-specific styled components ─────────────────────────────────────

const FormRG = styled(Form<FormValues>).attrs({ layout: "vertical" as const })`
  &.ant-form {
    display: contents;
  }

  .ant-form-item-explain-error {
    font-size: 12px;
  }
`;
